export type PlayerColor = "red" | "green" | "yellow" | "blue";

export const ALL_COLORS: PlayerColor[] = ["red", "green", "yellow", "blue"];

export const COLOR_HEX: Record<PlayerColor, string> = {
  red: "#e74c3c",
  green: "#2ecc71",
  yellow: "#f1c40f",
  blue: "#3498db",
};

export const COLOR_LABELS: Record<PlayerColor, string> = {
  red: "Red", green: "Green", yellow: "Yellow", blue: "Blue",
};

export interface Token {
  id: number;        // 0-3
  pos: number;       // -1 = base, 0-55 = path (0-51 common + 52-56 home col), 57 = home
}

export interface LadooPlayer {
  color: PlayerColor;
  name: string;
  tokens: Token[];
  isCPU: boolean;
}

export interface LadooGame {
  players: LadooPlayer[];
  turn: number;
  dice: number | null;
  rolled: boolean;
  winner: number | null;
  sixCount: number;
}

// ---- board layout (15×15 CSS grid) ---------------------------------------

export interface CellPos { row: number; col: number }

// 52 common path cells, clockwise from Red's start.
// Grid is 15×15 (0-14). Corners are bases (3×3 each).
// Path goes around the cross-shaped middle.
export const PATH_GRID: CellPos[] = (() => {
  const p: CellPos[] = [];

  // Red start at [6,1], going right along row 6
  for (let c = 1; c <= 5; c++) p.push({ row: 6, col: c });
  // Up along col 6
  for (let r = 5; r >= 0; r--) p.push({ row: r, col: 6 });
  // Right along row 0
  p.push({ row: 0, col: 7 }, { row: 0, col: 8 });
  // Down along col 8
  for (let r = 1; r <= 5; r++) p.push({ row: r, col: 8 });
  // Right along row 6
  for (let c = 9; c <= 13; c++) p.push({ row: 6, col: c });
  // Down along col 13
  p.push({ row: 7, col: 13 }, { row: 8, col: 13 });
  // Left along row 8
  for (let c = 12; c >= 7; c--) p.push({ row: 8, col: c });
  // Down along col 8
  for (let r = 9; r <= 13; r++) p.push({ row: r, col: 8 });
  // Left along row 13
  p.push({ row: 13, col: 7 }, { row: 13, col: 6 });
  // Up along col 6
  for (let r = 12; r >= 8; r--) p.push({ row: r, col: 6 });
  // Left along row 8
  for (let c = 6; c >= 0; c--) p.push({ row: 8, col: c });
  // Up along col 0
  p.push({ row: 7, col: 0 }, { row: 6, col: 0 });

  return p;
})();

// Player start positions (index into PATH_GRID)
export const PLAYER_START: Record<PlayerColor, number> = {
  red: 0, green: 13, yellow: 26, blue: 39,
};

// Home column cells (5 per player, leading toward center)
export const HOME_COLS: Record<PlayerColor, CellPos[]> = {
  red:    [{ row: 5, col: 1 }, { row: 5, col: 2 }, { row: 5, col: 3 }, { row: 5, col: 4 }, { row: 5, col: 5 }],
  green:  [{ row: 1, col: 8 }, { row: 2, col: 8 }, { row: 3, col: 8 }, { row: 4, col: 8 }, { row: 5, col: 8 }],
  yellow: [{ row: 8, col: 13 }, { row: 8, col: 12 }, { row: 8, col: 11 }, { row: 8, col: 10 }, { row: 8, col: 9 }],
  blue:   [{ row: 13, col: 6 }, { row: 12, col: 6 }, { row: 11, col: 6 }, { row: 10, col: 6 }, { row: 9, col: 6 }],
};

// Safe-zone path indices (last 7 cells before home column entry)
export function isSafeZone(pos: number, color: PlayerColor): boolean {
  const start = PLAYER_START[color];
  const entry = (start + 51) % 52; // position before entering home column
  // Safe zone: last 7 cells of this player's path segment
  for (let i = 0; i < 7; i++) {
    if (pos === (entry - i + 52) % 52) return true;
  }
  return false;
}

// Base grid positions (3×3 areas in corners)
export const BASE_CELLS: Record<PlayerColor, CellPos[]> = {
  red:    [{ row: 7, col: 1 }, { row: 7, col: 2 }, { row: 8, col: 1 }, { row: 8, col: 2 }],
  green:  [{ row: 7, col: 12 }, { row: 7, col: 13 }, { row: 8, col: 12 }, { row: 8, col: 13 }],
  yellow: [{ row: 0, col: 1 }, { row: 0, col: 2 }, { row: 0, col: 3 }, { row: 1, col: 0 }], // top-left corner
  blue:   [{ row: 13, col: 12 }, { row: 13, col: 13 }, { row: 14, col: 12 }, { row: 14, col: 13 }],
};

// Center/home cells
export const CENTER_CELLS: CellPos[] = [
  { row: 6, col: 6 }, { row: 6, col: 7 }, { row: 7, col: 6 }, { row: 7, col: 7 },
];